/**
 * Feature 021/022 — chapter pagination.
 *
 * Page numbers are not authored into the chapter HTML: they are derived here, so the reader
 * (`ChapterPage`) and the notes search (`utils/notesSearch.ts`) MUST share this module or a search
 * result would cite a page number the reader does not agree with. The split is device-independent
 * (weights, not measured pixels), so every device and both callers get identical page breaks.
 *
 * Pagination is grouped at hand/heading boundaries (never mid-hand) and sized by estimated content
 * WEIGHT, not by unit count — some chapters are ~190 hands, others are a couple dozen short sections
 * that are mostly bare headings. TARGET_PAGE_WEIGHT is the MINIMUM weight (~ rendered text lines) a
 * page must reach before it may end — sized to ~1.2–1.5 desktop screens so a page always continues
 * past the fold instead of stopping short and leaving dead space above the pager. MAX_PAGES caps the
 * longest chapter (the budget grows if a chapter would otherwise exceed it).
 */

const TARGET_PAGE_WEIGHT = 48;
const MAX_PAGES = 30;

/** Heading tags that open a section and may carry a deep-link anchor. */
const HEADING_SELECTOR = 'h1, h2, h3, h4';

/** The id stamped on the nth heading (document order) so a search result can deep-link a section. */
export function sectionAnchorId(headingIndex: number): string {
  return `b-sec-${headingIndex}`;
}

/**
 * Estimates a unit's rendered size in "line units" (~one line of text ≈ 1). Paragraphs scale with
 * text length; headings/rules are a couple of lines; tables are modelled by role so a hand stays
 * compact but a long auction/answer block counts for its height. Used only to balance page fullness.
 */
function estimateWeight(el: Element): number {
  const tag = el.tagName.toLowerCase();
  if (tag === 'hr') return 1;
  if (tag === 'h1' || tag === 'h2') return 3;
  if (tag === 'h3' || tag === 'h4') return 2;
  if (tag === 'table') {
    if (el.classList.contains('b-sidebyside')) {
      // A hand laid out beside its auction/answer: height ≈ the taller column plus the comment lines
      // (question/answer/attribution) that stack under the auction on the right.
      const cardLines = el.querySelectorAll('.b-cardline').length;
      const auctionRows = el.querySelectorAll('.b-auction-grid tr').length;
      const gridParas = el.querySelectorAll('.b-auction-grid p').length;
      const commentParas = el.querySelectorAll('p').length - cardLines - gridParas;
      return Math.max(cardLines, auctionRows) + Math.max(0, commentParas);
    }
    // A standalone bid grid — one line per row.
    return Math.max(1, el.querySelectorAll('tr').length);
  }
  const text = el.textContent ?? '';
  return Math.max(1, Math.ceil(text.length / 60));
}

/**
 * Splits a parsed chapter into pages of top-level elements, breaking ONLY at hand/heading boundaries
 * so a hand and its auction/answer are never separated. Each "unit" starts at a heading (h1–h4) or a
 * "You Hold" intro (`.b-handintro`) and runs to the next boundary. Units are packed greedily up to a
 * target weight per page (raised if the chapter would otherwise exceed MAX_PAGES), so a light chapter
 * of mostly short headings fills each page instead of leaving one heading stranded per page.
 *
 * Returns one array of elements per page. Callers that want markup should use `paginate`; callers
 * that need to locate an element's page (search) use this and read the grouping directly.
 */
export function paginateDocument(doc: Document): Element[][] {
  const blocks = Array.from(doc.body.children);
  if (blocks.length === 0) return [];

  const isBoundary = (el: Element): boolean => {
    const tag = el.tagName.toLowerCase();
    return tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || el.classList.contains('b-handintro');
  };
  const isSeparator = (el: Element | null): boolean =>
    !!el && el.tagName.toLowerCase() === 'hr' && el.classList.contains('b-separator');

  // A "unit" is one problem/item that must not be split across a page break. Units start at a heading
  // or a "You Hold" hand intro, AND after every between-problem separator (`<hr class="b-separator">`)
  // — so a long run of standalone questions under one heading (e.g. "Other 1♦ sequences", 13 problems)
  // flows across pages instead of gluing into one tall block that strands the previous page. Separators
  // only ever sit *between* complete problems, so this never splits a hand from its auction.
  const units: Element[][] = [];
  let prev: Element | null = null;
  for (const el of blocks) {
    const current = units[units.length - 1];
    if (!current || isBoundary(el) || isSeparator(prev)) units.push([el]);
    else current.push(el);
    prev = el;
  }

  const weights = units.map((u) => u.reduce((sum, el) => sum + estimateWeight(el), 0));

  // A unit that is nothing but headings (and/or a rule) — a page must not END on one of these, or a
  // section title gets stranded away from the content it introduces.
  const isHeadingOnly = (unit: Element[]): boolean =>
    unit.every((el) => /^h[1-4]$/.test(el.tagName.toLowerCase()) || el.tagName.toLowerCase() === 'hr');

  // Pack units with OVERSHOOT: a page may only end once its weight has REACHED the budget, so every
  // page except the last renders at least a full screen — content always continues past the fold
  // instead of stopping short and leaving dead space above the pager (the old break-before-overflow
  // packing did exactly that whenever the next unit was large). Trailing heading-only units are
  // pushed onto the next page so a title always opens the content it belongs to.
  const pack = (budget: number): Element[][] => {
    const out: Element[][] = [];
    let i = 0;
    while (i < units.length) {
      const taken: number[] = [];
      let w = 0;
      while (i < units.length && w < budget) {
        taken.push(i);
        w += weights[i] ?? 0;
        i += 1;
      }
      while (taken.length > 1 && i < units.length && isHeadingOnly(units[taken[taken.length - 1]!]!)) {
        taken.pop();
        i -= 1;
      }
      out.push(taken.flatMap((k) => units[k]!));
    }
    return out;
  };

  // Start from the target page fullness and grow the budget only if the chapter would exceed
  // MAX_PAGES, so light chapters keep viewport-filling pages while the longest ones stay capped.
  let budget = TARGET_PAGE_WEIGHT;
  let grouped = pack(budget);
  while (grouped.length > MAX_PAGES) {
    budget += Math.max(4, Math.ceil(budget * 0.15));
    grouped = pack(budget);
  }

  // With overshoot packing only the FINAL page can be light (a short remainder); fold it into the
  // previous page rather than ending the chapter on a mostly empty page.
  if (grouped.length > 1) {
    const last = grouped[grouped.length - 1]!;
    const lastWeight = last.reduce((s, el) => s + estimateWeight(el), 0);
    if (lastWeight < budget / 2) {
      grouped.pop();
      grouped[grouped.length - 1] = grouped[grouped.length - 1]!.concat(last);
    }
  }

  return grouped;
}

/**
 * Stamp each heading with a stable anchor id keyed by its document-order index. The index is derived
 * from the parsed document, not from the page split, so `notesSearch` can name an anchor without
 * paginating and both sides always agree.
 *
 * The id is assigned unconditionally: an authored id would otherwise win here while `notesSearch`
 * still cited `b-sec-N`, leaving a section link pointing at nothing. The converter emits bare
 * headings today, so nothing is being overwritten.
 */
export function stampSectionAnchors(doc: Document): void {
  Array.from(doc.querySelectorAll(HEADING_SELECTOR)).forEach((h, i) => {
    h.id = sectionAnchorId(i);
  });
}

/**
 * Marks the header row of every visible auction grid so the stylesheet can give it the design's
 * felt-green column-header treatment.
 *
 * The converter emits no `<th>`: a grid's header row is authored as ordinary cells whose only
 * content is a bolded label (seat markers like "P" / "Op", or column titles like "Response").
 * That "every cell is a lone `<p><strong>`" shape is the reliable signal — a row of actual calls
 * always carries at least one unbolded cell, so a bid row can never be mistaken for a header.
 *
 * Decoration is class-only (no `td` → `th` rewrite) so `estimateWeight`'s row/paragraph counts,
 * `notesSearch.extractAuctionCalls`'s `<strong>` skip, and every existing selector keep seeing
 * exactly the markup they see today — the page split is byte-identical before and after.
 */
export function decorateChapterDoc(doc: Document): void {
  Array.from(doc.querySelectorAll('table.b-auction-grid')).forEach((grid) => {
    const firstRow = grid.querySelector('tr');
    if (!firstRow) return;

    const cells = Array.from(firstRow.children).filter((c) => /^(td|th)$/i.test(c.tagName));
    if (cells.length === 0) return;

    const isBoldLabel = (cell: Element): boolean => {
      if (cell.children.length !== 1) return false;
      const p = cell.children[0]!;
      if (!/^p$/i.test(p.tagName)) return false;
      return p.children.length === 1 && /^strong$/i.test(p.children[0]!.tagName);
    };
    if (!cells.every(isBoldLabel)) return;

    grid.classList.add('b-notes-grid');
    cells.forEach((cell) => cell.classList.add('b-colhead'));
  });
}

/** Splits a chapter's content HTML into pages of markup, ready to render. */
export function paginate(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  stampSectionAnchors(doc);
  decorateChapterDoc(doc);
  const grouped = paginateDocument(doc);
  if (grouped.length === 0) return [html];
  const pages = grouped.map((els) => els.map((el) => el.outerHTML).join(''));
  return pages.length ? pages : [html];
}
